import { EMBED_PARAM } from "../hooks/useIsEmbedded";
import { EMBED_FLAECHE_PARAM } from "./embed-flaeche";
import { EMBED_FARBTHEMA_PARAM } from "./embed-theme";
import { EMBED_WIRT_PARAM } from "./embed-navigation";

/**
 * Die App-Huelle im Embed vor dem ERSTEN BILD wegnehmen — nicht nach der Hydration.
 *
 * ── DER FEHLER, GEGEN DEN DAS HIER STEHT ────────────────────────────────────
 *
 * `useIsEmbedded` startet mit `false` und wird erst im Effekt `true`
 * (`useIsEmbedded.ts`). Der Server rendert die Kopfzeile deshalb IMMER mit, auch
 * wenn `?embed=1` an der Adresse steht — sie steht im ausgelieferten HTML und
 * wird gezeichnet, bis die Hydration sie abraeumt. Am 2026-09-17 an der
 * laufenden `legal.janewaylabs.com/?embed=1` gemessen (headless Chrome, leerer
 * Cache, drei Laeufe):
 *
 *     FCP                          140–220 ms
 *     Kopfzeile im DOM bis         277–398 ms
 *     danach weg ab                285–407 ms
 *     SICHTBAR also                145–194 ms, 56 px hoch
 *
 * 56 px, die erscheinen und wieder verschwinden: das ist der „Versatz", den der
 * Betreiber am Seitenpanel gemeldet hat. Der Inhalt springt beim Abraeumen um
 * genau diese Hoehe nach oben.
 *
 * ── WARUM NICHT DER SERVER ──────────────────────────────────────────────────
 *
 * Der naheliegende Weg waere, `?embed=1` serverseitig zu lesen und die Kopfzeile
 * gar nicht erst zu rendern. Beide Tueren dorthin sind zu:
 *
 *  1. **Ein Layout bekommt `searchParams` nicht.** „Layouts do not rerender on
 *     navigation, so they cannot access search params" — Next 16.2.4,
 *     `docs/01-app/03-api-reference/03-file-conventions/layout.md`. Nur eine
 *     `page.tsx` bekommt sie; die Huelle haengt aber im Layout. Den Wert von
 *     jeder Seite jeder App durchzureichen waere eine Bringschuld pro SEITE.
 *  2. **`useSearchParams` ist in dieser Bibliothek gesperrt**, und zwar aus
 *     gemessenem Grund: er zwingt jede statisch erzeugte Seite ohne eigene
 *     Suspense-Grenze in CSR und bricht dort den BAU DER APP, nicht unseren
 *     (`components/settings/SettingsPage.tsx:69`, dieselbe Entscheidung in
 *     `auth/onboarding/AuthOnboarding.tsx:699`). Beide nennen `useIsEmbedded`
 *     als das Muster, dem sie folgen — ausgerechnet.
 *
 * ── UND WARUM KEIN REACT-WEG ────────────────────────────────────────────────
 *
 * Es gibt keinen. Der Kopf wird gezeichnet, sobald der Parser sein Markup
 * erreicht; jede Client-Entscheidung — Effekt, `useSyncExternalStore`, ein
 * anderer Anfangszustand — faellt danach. Nur zwei Dinge liegen davor: CSS und
 * ein blockierendes Inline-Skript. Genau diese Rechnung steht schon einmal in
 * `embed-flaeche.ts` (dort fuer die Flaeche, mit denselben Messwerten), und der
 * Design-Reiter hat denselben Schluss fuer den Hell/Dunkel-Modus gezogen:
 * beide Beschriftungen rendern, CSS entscheidet (`settings/DesignTab.tsx:395`).
 *
 * **Das Skript versteckt nichts nachtraeglich.** Es steht im Markup VOR der
 * Kopfzeile; der Parser fuehrt es aus, bevor er ihr Element ueberhaupt anlegt.
 * Gezeichnet wird sie damit nie — sie belegt auch keinen Platz, also gibt es
 * auch den Sprung nicht mehr.
 *
 * ── WARUM NICHT IN DEN SEITENKOPF ───────────────────────────────────────────
 *
 * Weil der jeder App gehoert: ein Skript dort waere 23-mal einzutragen und
 * 23-mal zu vergessen. `embedFlaecheVorpaintSkript` steht seit dem 2026-09-16
 * genau deshalb in KEINER App (gemessen: kein Treffer ausserhalb dieser
 * Bibliothek). `AppLayout` gibt beides selbst aus — wer die Huelle rendert, hat
 * es.
 */

/** Marke am Wurzelknoten: diese Seite laeuft in der Huelle eines Einbetters. */
export const EMBED_MARKE = "data-jw-eingebettet";

/** Marke an der schmalen V2-Kopfzeile — V1s `TopNav` traegt `data-jw-appheader`. */
export const MOBILKOPF_MARKE = "data-jw-mobilkopf";

/**
 * Marke am Abo-Banner.
 *
 * Der dritte Huellenteil, und der einzige, der im Embed nicht blitzt, sondern
 * STEHENBLEIBT: seine Bedingung ist allein der Abo-Status, eine Embed-Pruefung
 * hat er nicht. Gemountet in 13 Apps, darunter jede aus der Panel-Liste.
 */
export const ABOBANNER_MARKE = "data-jw-abobanner";

/** Marke an der Huelle um die Seitenleiste (`AppLayout`). */
export const LEISTE_MARKE = "data-jw-huellenleiste";

/**
 * Die Regel, die die Huelle wegnimmt.
 *
 * `!important`, obwohl die Spezifitaet schon reicht: die Kopfzeile bekommt unter
 * `auftritt` einen Inline-Stil (`huellen-auftritt.ts`), und ein Inline-Stil
 * schlaegt jede Regel ohne `!important`. Dieselbe Vorsichtsmassnahme wie bei der
 * Gegenregel zu `prefers-reduced-motion` in derselben Datei.
 *
 * ── WARUM AUCH DIE SEITENLEISTE, SEIT 2026-09-21 ────────────────────────────
 *
 * Weil „eingebettet" sonst an einem Umbruchpunkt haengt statt an der Einbettung.
 * Die Leiste trug `hidden md:block`, und ein `md:` im iframe misst die Breite
 * des PANELS: unter 768 px war sie weg, darueber kam sie zurueck. Die
 * Standardstufe des Panels ist 800 px (`artifact/panel-breite.ts`, `START_PANEL`
 * — bewusst ueber md, sonst haelt sich der Workflow-Editor fuer ein Telefon),
 * also war der Normalfall der mit Leiste.
 *
 * Am 2026-09-21 im Showroom-Panel gemessen, drei Gaeste, je 380 / 700 / 800 px:
 *
 *     380 px   1 Kopf (Panel-Kopf des Wirts)     keine Leiste
 *     700 px   1 Kopf                            keine Leiste
 *     800 px   2 Koepfe                          Leiste 176–226 px
 *
 * Bei 800 px stand der App-Name zweimal untereinander — einmal im Panel-Kopf des
 * Wirts, 56 px darunter noch einmal im Kopf der Leiste. Betreiber am 2026-09-21:
 * „dann kommt irgendwann die Seitenleiste der eigentlichen App und dann haben
 * wir den Titel doppelt … Sollen wir die Seitenleiste komplett entfernen".
 *
 * Der Weg zur Navigation ist damit ausschliesslich der Menueknopf im Panel-Kopf
 * (`artifact/AppEmbedArtifact.tsx`), der das Schubfach des Gastes ueber die
 * Navigations-Bruecke aufklappt. Die Regel steht hier und nicht in React, aus
 * demselben Grund wie fuer die Kopfzeile darueber: `useIsEmbedded` antwortet
 * erst nach der Hydration, die Leiste blitzte sonst auf und klappte zusammen.
 */
export const EMBED_HUELLE_CSS =
  `html[${EMBED_MARKE}] [data-jw-appheader],` +
  `html[${EMBED_MARKE}] [${MOBILKOPF_MARKE}],` +
  `html[${EMBED_MARKE}] [${LEISTE_MARKE}],` +
  `html[${EMBED_MARKE}] [${ABOBANNER_MARKE}]{display:none!important}` +
  /* ── UND DIE KLAMMER FAELLT SCHON HIER, NICHT ERST IN DER EXTERNEN CSS ─────
     Ein Wettrennen, das wir am 2026-09-21 verloren im Bild gesehen haben.

     Die Klammer kommt INLINE an den Knoten: `schalenStil()` schreibt
     `--jw-radius: 1.25rem` als style-Attribut (`layout/huellen-rezept.ts`).
     Die Gegenregel steht in `globals.css` — einer EXTERNEN Datei. Solange die
     nicht angewandt ist, gibt es die Gegenregel schlicht nicht, und der
     Inline-Wert gilt.

     Gemessen an `calendar` (die schwerste der Panel-Seiten): der Gast stand mit
     Ersatzschrift, ungestylten Knoepfen und nativer Bildlaufleiste da — also
     ohne sein Stylesheet — und trug dabei `--jw-radius: 1.25rem` bei
     `backgroundColor: rgba(0,0,0,0)`. Volle Klammer: runde Ecken, Rinne, und
     die Inhaltskarte auf `--jw-tafel`. Der Betreiber hat genau das gemeldet
     („bei Kalender bin ich mir hundertprozentig sicher, dass es da rund ist"),
     und es ist der Grund, warum sechs Farbrunden nichts geaendert haben.

     Warum es nicht reicht, dass `AppLayout` eingebettet `flach` rendert: das
     weiss erst der Browser. `window.top` gibt es serverseitig nicht, das
     SSR-Bild traegt also weiter die Klammer inline. Diese Regel gilt dagegen,
     sobald der Parser den Kopf gelesen hat.

     Dieselben Namen wie die Regel in `globals.css`; dass die beiden nicht
     auseinanderlaufen, haelt
     `__tests__/eingebettet-rendert-die-flache-schale.test.tsx` fest. */
  `html[${EMBED_MARKE}] [data-jw-flaeche]{` +
  "--jw-rinne:0px!important;" +
  "--jw-radius:0px!important;" +
  "--jw-grund:var(--background)!important;" +
  "--jw-grund-vordergrund:var(--foreground)!important;" +
  "--jw-inhalt:var(--background)!important}" +
  /* ── UND KEIN SCHLAGSCHATTEN ───────────────────────────────────────────────
     Ein Schatten sagt „diese Karte liegt AUF etwas". Im Panel liegt sie auf
     nichts — es gibt genau eine Flaeche, die des Einbetters. Er malt dann einen
     Rand um einen Kasten, der keiner sein soll.

     Der Rest, der nach der Flachstellung uebrig blieb: am 2026-09-22 im echten
     Seitenpanel in ALLEN geprueften Apps gemessen, und nur VOR der Hydration —
     `color(srgb 0 0 0 / 0.12) 0px 1px 2px 0px`. Danach rendert `AppLayout`
     `flach` und die Regel auf `umklammert` greift nicht mehr; das SSR-Bild
     traegt sie aber weiter, denn serverseitig ist die Einbettung nicht
     erkennbar.

     Warum eine EIGENE Zeile und nicht die Variablen darueber: `--jw-radius`
     und die Flaechen sind Variablen, `box-shadow` ist keine. Sie muss
     ausdruecklich zurueckgenommen werden.

     NUR die Inhaltskarte. Die Nebenkarte stand hier einen Entwurf lang mit
     dabei und war falsch: sie hat unter „umklammert" laengst `box-shadow: none`
     (`globals.css`, Block „Ein Koerper, der seine eigene Flaeche traegt"). Der
     Waechter `embed-huelle-vor-dem-ersten-bild` hat es gemeldet — er prueft,
     dass jede adressierte Marke im gerenderten Markup ueberhaupt vorkommt, und
     eine Regel ins Leere ist keine Absicherung, sondern eine Behauptung. */
  `html[${EMBED_MARKE}] [data-jw-inhaltskarte]{box-shadow:none!important}`;

/**
 * Das Inline-Skript, das die Marke setzt.
 *
 * Es haelt dieselben zwei Signale wie `useIsEmbedded`, ODER-verknuepft, und ist
 * damit die einzige Stelle, an der die iframe-Erkennung ueberhaupt frueh genug
 * kommt — serverseitig ist sie unmoeglich, denn `window.top` kennt nur der
 * Browser.
 *
 * **Kein `toString()` einer echten Funktion**, und das ist hier keine
 * Geschmacksfrage: esbuild instrumentiert transpilierte Funktionen mit seinem
 * Helfer `__name(...)`, den es in einem Inline-Skript nicht gibt. Der
 * `ReferenceError` landet im `catch` und die Marke kommt nie an — ohne Fehler,
 * ohne Spur. Am 2026-09-16 an `embedFlaecheVorpaintSkript` genau so gemessen,
 * waehrend der Unit-Test gruen war (unter vitest steht `__name` im
 * Geltungsbereich). Der Waechter zu dieser Datei blendet ihn deshalb aus.
 */
export function embedVorpaintSkript(): string {
  const marke = JSON.stringify(EMBED_MARKE);
  return [
    "(function(){try{",
    `if(window.self===window.top&&new URLSearchParams(location.search).get(${JSON.stringify(EMBED_PARAM)})!=='1')return;`,
    "var w=document.documentElement;",
    `w.setAttribute(${marke},'');`,
    /* ── UND SIE BLEIBT AUCH, WENN REACT DEN WURZELKNOTEN NEU AUFBAUT ────────
       Gemessen am 2026-09-22 im echten Seitenpanel, drei von drei Laeufen,
       `janeway-automations`:

           t= 524  setAttribute data-jw-eingebettet   = ""
           t=1153  mutation lang                  null → "de"
           t=1153  mutation class                 null → "…dark"
           t=1153  mutation style                 null → "--background: hsl(244 40% 6%) …"
           t=1153  mutation data-jw-eingebettet   ""   → null

       `lang`, `class` und `style` springen von NULL auf einen Wert — das ist
       kein Attribut-Update, sondern ein frisch aufgebauter Wurzelknoten. React
       verwirft das Server-`<html>` und befuellt es neu, und dabei faellt alles
       weg, was nicht aus dem Server-Markup stammt. Genau das sind unsere
       Marken: der Server weiss nicht, dass er eingebettet ist, `window.top`
       gibt es dort nicht.

       Der Preis war die ganze Klammer — 363 bis 410 ms lang Inhaltskarte
       `rgb(46,44,63)` (`--jw-tafel`) und Radius 20px, bis die Hydration `flach`
       rendert. Der Betreiber hat genau das gemeldet und 36 Stunden darauf
       gewartet, dass es jemand misst statt es zu erklaeren.

       WARUM DIE MARKE SICH SELBST HAELT UND WIR NICHT DIE URSACHE SUCHEN:
       warum React ausgerechnet dort den Knoten neu baut, ist ein zweiter
       Fehler, und er kann morgen in einer anderen App auftauchen, sobald sich
       eine Next-Einstellung aendert. Die Zusage „im Panel gilt die flache
       Schale" darf nicht davon abhaengen, ob ein Framework einen Knoten
       wiederverwendet.

       Der Beobachter haengt am KNOTEN, und das traegt, weil React denselben
       `<html>` neu befuellt statt ihn zu ersetzen — die Mutationen oben sind an
       ihm gemessen. Keine Schleife: das eigene `setAttribute` meldet zwar
       zurueck, beim Nachsehen steht das Attribut dann aber schon da. */
    "if(typeof MutationObserver!=='function')return;",
    `new MutationObserver(function(){if(!w.hasAttribute(${marke}))w.setAttribute(${marke},'')})`,
    `.observe(w,{attributes:true,attributeFilter:[${marke}]});`,
    "}catch(e){}})()",
  ].join("");
}


/**
 * Die vier Parameter, an denen die Einbettung haengt — und die JEDE
 * Adressaenderung der Gast-App ueberleben muessen.
 *
 * ── DER BEFUND ──────────────────────────────────────────────────────────────
 *
 * Der Wirt haengt sie an die iframe-Adresse (`AppEmbedArtifact`), weil nur so
 * schon das ERSTE Bild stimmt (Begruendung im Kopf von `embed-flaeche.ts`).
 * Danach gehoert die Adresse aber der Gast-App, und mehrere Apps schreiben sie
 * NEU, statt sie zu ergaenzen. `janeway-calendar` tut es auf jeder Ansicht:
 *
 *     const url = `${basePath}/calendar?view=${view}&date=${dateStr}`;
 *     window.history.replaceState(null, "", url);
 *
 * Am 2026-09-21 im echten Seitenpanel gemessen (`chat.janewaylabs.com`,
 * Theme `cosmic-night` dunkel, Flaeche `#0a0915`):
 *
 *     geladen   /calendar?embed=1&flaeche=%230a0915&farbthema=…&wirt=…   Antwort 200
 *     danach    /calendar?view=month&date=2026-09-21                     alles vier WEG
 *
 * Solange das Dokument steht, faellt es nicht auf: Marke und Farben hat das
 * Vorpaint-Skript schon gesetzt. Beim naechsten LADEN derselben Adresse ist die
 * App aber keine eingebettete mehr — sie baut ihre volle Huelle auf und malt
 * ihre Inhaltskarte mit dem EIGENEN Ton. Im Theme des Betreibers ist das
 * `--jw-tafel` = `#2e2c3f` auf einer Seitenleiste in `#0a0915`: „Kalender ist
 * immer noch grau anstatt dieses dunkle Blau" (2026-09-21).
 *
 * ── WARUM ZENTRAL UND NICHT JE APP ──────────────────────────────────────────
 *
 * Weil es nicht eine Stelle ist. `history.replaceState`/`pushState` mit einer
 * frisch gebauten Adresse steht in calendar, notes, messenger, drive, cashflow
 * und mail-client, und jede kuenftige Ansicht mit Zustand in der Adresse kommt
 * dazu. Eine Regel, die man bei jedem neuen Aufruf mitdenken muss, ist keine
 * Regel — dieselbe Lehre wie bei den Waechtern in der `CLAUDE.md` der Suite:
 * erst die REICHWEITE messen, dann den Fix bauen.
 *
 * Der Griff ist eng gefasst: nur im Embed, nur gleiche Herkunft, nur diese vier
 * Namen, und nur wenn sie in der neuen Adresse FEHLEN. Eine App, die `flaeche`
 * selbst setzt, behaelt ihren Wert.
 */
export const EMBED_ADRESSPARAMETER: readonly string[] = [
  EMBED_PARAM,
  EMBED_FLAECHE_PARAM,
  EMBED_FARBTHEMA_PARAM,
  EMBED_WIRT_PARAM,
];

/**
 * Das Inline-Skript, das die vier Parameter an jeder kuenftigen Adresse haelt.
 *
 * Es laeuft VOR dem Framework-Bundle, also faengt es auch Next auf: der
 * App-Router legt seinen eigenen Griff um `history.pushState` und ruft dabei
 * den vor, den er vorfindet — unseren.
 *
 * **Kein `toString()` einer echten Funktion** — derselbe Grund wie eine Ebene
 * darueber (esbuilds `__name`-Helfer gibt es im Inline-Skript nicht).
 */
export function embedAdresseVorpaintSkript(): string {
  const namen = JSON.stringify(EMBED_ADRESSPARAMETER);
  return [
    "(function(){try{",
    `if(window.self===window.top&&new URLSearchParams(location.search).get(${JSON.stringify(EMBED_PARAM)})!=='1')return;`,
    `var N=${namen};var s=new URLSearchParams(location.search);var h={};`,
    "for(var i=0;i<N.length;i++){var v=s.get(N[i]);if(v!==null)h[N[i]]=v}",
    "if(!Object.keys(h).length)return;",
    "var H=window.history;var f=function(o){return function(a,b,u){",
    "try{if(u!==null&&u!==undefined){var x=new URL(String(u),location.href);",
    "if(x.origin===location.origin){for(var k in h){if(!x.searchParams.has(k))x.searchParams.set(k,h[k])}",
    "u=x.pathname+x.search+x.hash}}}catch(e){}",
    "return u===undefined?o.call(H,a,b):o.call(H,a,b,u)}};",
    "H.pushState=f(H.pushState);H.replaceState=f(H.replaceState);",
    "}catch(e){}})()",
  ].join("");
}
